import React, { useState } from 'react';
import styled from 'styled-components';

const PRIMARY = '#2DBCAF';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 24px;
  padding: 28px 24px 24px;
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
  font-family: 'Plus Jakarta Sans', sans-serif;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 6px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  text-align: center;
  margin: 0 0 24px;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 4px;
  span.required { color: #ef4444; }
  span.optional { color: #94a3b8; font-weight: 500; }
`;

const Input = styled.input`
  height: 48px;
  padding: 0 14px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 15px;
  font-family: inherit;
  color: #0f172a;
  outline: none;
  -webkit-appearance: none;
  transition: border 0.2s, box-shadow 0.2s;
  &:focus {
    border-color: ${PRIMARY};
    box-shadow: 0 0 0 3px rgba(45,188,175,0.15);
  }
  &::placeholder { color: #94a3b8; }
  &[type="date"] {
    &::-webkit-date-and-time-value {
      text-align: left;
    }
    &:invalid::-webkit-datetime-edit {
      color: #94a3b8;
    }
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const SubmitBtn = styled.button`
  height: 52px;
  background: ${PRIMARY};
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  margin-top: 8px;
  transition: opacity 0.2s, transform 0.1s;
  &:hover { opacity: 0.9; }
  &:active { transform: scale(0.98); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorMsg = styled.p`
  font-size: 13px;
  color: #ef4444;
  margin: 0;
  text-align: center;
`;

const IconCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(45,188,175,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  .material-symbols-outlined {
    font-size: 32px;
    color: ${PRIMARY};
  }
`;

interface Props {
  onComplete: () => void;
}

export default function ChildOnboardingModal({ onComplete }: Props) {
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !birthDate) {
      setError('Nama dan tanggal lahir wajib diisi');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/children', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          birthDate,
          height: height ? Number(height) : undefined,
          weight: weight ? Number(weight) : undefined,
        }),
      });

      const data = await res.json();
      if (data.ok) {
        onComplete();
      } else {
        setError(data.error || 'Gagal menyimpan data');
      }
    } catch {
      setError('Terjadi kesalahan. Coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay>
      <Modal>
        <IconCircle>
          <span className="material-symbols-outlined">child_care</span>
        </IconCircle>
        <Title>Tambahkan Anak</Title>
        <Subtitle>
          Silakan masukkan data anak Anda untuk pengalaman bermain yang lebih personal.
        </Subtitle>

        <Form onSubmit={handleSubmit}>
          <Field>
            <Label>Nama Anak <span className="required">*</span></Label>
            <Input
              type="text"
              placeholder="Masukkan nama anak"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </Field>

          <Field>
            <Label>Tanggal Lahir <span className="required">*</span></Label>
            <Input
              type="date"
              value={birthDate}
              onChange={e => setBirthDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              required
            />
          </Field>

          <Row>
            <Field>
              <Label>Tinggi Badan <span className="optional">(opsional)</span></Label>
              <Input
                type="number"
                placeholder="cm"
                value={height}
                onChange={e => setHeight(e.target.value)}
                min="30"
                max="200"
              />
            </Field>
            <Field>
              <Label>Berat Badan <span className="optional">(opsional)</span></Label>
              <Input
                type="number"
                placeholder="kg"
                value={weight}
                onChange={e => setWeight(e.target.value)}
                min="3"
                max="100"
              />
            </Field>
          </Row>

          {error && <ErrorMsg>{error}</ErrorMsg>}

          <SubmitBtn type="submit" disabled={loading}>
            {loading ? 'Menyimpan...' : 'Simpan & Lanjutkan'}
          </SubmitBtn>
        </Form>
      </Modal>
    </Overlay>
  );
}
